'use client'

import type { Fetchable } from '@morpho-org/blue-sdk'
import type { Time } from '@morpho-org/morpho-ts'

import { type OptionSelect, Select } from '@morpho-org/ui-v6/client'
import { formatters } from '@morpho-org/ui-v6/utils'

import { useHighchartsOptions } from '../hooks/useHighchartsOptions'
import SimpleChart, { type SimpleChartProps } from './SimpleChart'

export interface ChartHoverCardProps {
  title: string
  timeFrameOptions: OptionSelect<Time.PeriodLike | null>[]
  total: Fetchable<bigint>
  decimals: Fetchable<number>
  loading: SimpleChartProps['statuses']['loading']
  error: SimpleChartProps['statuses']['error']
  data: [number, number | null][]
  /** Shows a notice that some of the history is missing. */
  partial?: boolean
  timeFrame: Time.PeriodLike | null
  setTimeFrame: (timeFrame: Time.PeriodLike | null) => void
}

const ChartHoverCard = ({
  title,
  data,
  timeFrameOptions,
  total,
  decimals = 18,
  loading,
  error,
  partial = false,
  timeFrame,
  setTimeFrame
}: ChartHoverCardProps) => {
  const { palette } = useHighchartsOptions()

  const totalSupplyFormat = formatters.commas.trailingZero(false).digits(2).of(total, decimals)

  return (
    <div data-testid="chart-hover-card" className="gap-sm p-md flex w-full flex-col">
      <div className="gap-2xs flex flex-col">
        <div className="flex items-center justify-between py-[2px]">
          <span className="text-secondary-foreground text-xs">{title}</span>
          <Select<Time.PeriodLike | null>
            options={timeFrameOptions}
            value={timeFrame}
            onChange={setTimeFrame}
            nativeSelect
            size="sm"
          />
        </div>
        <div className="flex gap-[1px]">
          <span className="text-title-xs text-muted-foreground">$</span>
          <span className="text-title-xs text-foreground">{totalSupplyFormat}</span>
        </div>
      </div>

      <SimpleChart
        statuses={{
          loading,
          error
        }}
        unit="$"
        height="158px"
        noAnimation
        borderRadius={14}
        series={[
          {
            data,
            type: 'areaspline',
            name: 'Total supply',
            color: palette.colors[0],
            lineColor: palette.colors[0],
            fillColor: palette.areaChartColors[0]
          }
        ]}
      />
      {partial && !error && (
        <span data-testid="chart-hover-card-partial" className="text-secondary-foreground text-xs">
          Some of this history is missing.
        </span>
      )}
    </div>
  )
}

export default ChartHoverCard
